import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  QrCode, Siren, Users, Car, ShieldAlert, Search, MessageCircle, Activity, Plus, ArrowUpRight, Quote, Heart
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QUICK = [
  { to: "/app/qr", icon: QrCode, label: "New QR", color: "primary" },
  { to: "/app/sos", icon: Siren, label: "SOS", color: "destructive" },
  { to: "/app/family", icon: Users, label: "Family", color: "secondary" },
  { to: "/app/ai", icon: MessageCircle, label: "Guardian AI", color: "accent" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ qrs: 0, family: 0, sos: 0, community: 0 });
  const [recent, setRecent] = useState([]);
  const [storyOpen, setStoryOpen] = useState(false);
  const [story, setStory] = useState({ quote: "", city: "", stars: 5 });

  const submitStory = async () => {
    if (story.quote.trim().length < 20) return toast.error("Please write at least 20 characters");
    try {
      await api.post("/testimonials", story);
      toast.success("Thank you! Your story is pending moderation.");
      setStory({ quote: "", city: "", stars: 5 });
      setStoryOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  useEffect(() => {
    (async () => {
      const [qrs, family, sos, comm] = await Promise.all([
        api.get("/qr").catch(() => ({ data: [] })),
        api.get("/family").catch(() => ({ data: [] })),
        api.get("/sos/mine").catch(() => ({ data: [] })),
        api.get("/community").catch(() => ({ data: [] })),
      ]);
      setStats({
        qrs: qrs.data.length,
        family: family.data.length,
        sos: sos.data.length,
        community: comm.data.length,
      });
      setRecent(sos.data.slice(0, 5));
    })();
  }, []);

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Guardian Console</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">Hello, {user?.name?.split(" ")[0] || "Guardian"}.</h1>
        <p className="text-muted-foreground mt-1">You are protected. Everything below is live.</p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {QUICK.map((q, i) => (
          <motion.div key={q.to} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Link to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="group rounded-2xl border border-border/70 bg-card p-5 hover:border-[hsl(var(--primary))]/60 hover:-translate-y-0.5 transition-transform">
                <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 bg-[hsl(var(--${q.color}))]/10 group-hover:bg-[hsl(var(--${q.color}))] transition-colors`}>
                  <q.icon className={`w-5 h-5 text-[hsl(var(--${q.color}))] group-hover:text-white transition-colors`} />
                </div>
                <div className="font-semibold flex items-center justify-between">
                  {q.label} <ArrowUpRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Stats grid */}
      <div className="grid md:grid-cols-4 gap-3 mb-8">
        <Stat label="Active QR profiles" value={stats.qrs} icon={QrCode} />
        <Stat label="Family members" value={stats.family} icon={Users} />
        <Stat label="SOS alerts sent" value={stats.sos} icon={Siren} />
        <Stat label="Community reports" value={stats.community} icon={ShieldAlert} />
      </div>

      {/* Recent SOS */}
      <Card className="border-border/70">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display font-bold text-xl tracking-tight">Recent SOS activity</div>
              <div className="text-sm text-muted-foreground">Your last emergency alerts</div>
            </div>
            <Button variant="outline" size="sm" asChild data-testid="new-sos-btn">
              <Link to="/app/sos"><Plus className="w-4 h-4 mr-1" /> New</Link>
            </Button>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-12">No alerts yet. Stay safe.</div>
          ) : (
            <div className="space-y-2">
              {recent.map((a) => (
                <div key={a.id} className="flex items-center gap-4 py-3 border-b border-border/40 last:border-0">
                  <div className={`w-2.5 h-2.5 rounded-full ${a.status === "active" ? "bg-[hsl(var(--destructive))] sos-pulse" : "bg-[hsl(var(--secondary))]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{a.message || `${a.category} alert`}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()} · {a.latitude?.toFixed(3)}, {a.longitude?.toFixed(3)}
                    </div>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{a.status}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share your Guardian story */}
      <div className="mt-6 rounded-2xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-6 flex items-center gap-5 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))]/15 grid place-items-center shrink-0">
          <Quote className="w-5 h-5 text-[hsl(var(--accent))]" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold text-lg tracking-tight">Share your Guardian story</div>
          <div className="text-sm text-muted-foreground">A moment when Guardian360 helped you or a loved one. If approved, it may appear on our homepage.</div>
        </div>
        <Dialog open={storyOpen} onOpenChange={setStoryOpen}>
          <DialogTrigger asChild>
            <Button data-testid="share-story-btn" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
              <Heart className="w-4 h-4 mr-2" /> Share a story
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share your Guardian story</DialogTitle>
              <DialogDescription>Our team moderates every submission before it appears on the landing page.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Your story (min. 20 chars)</Label>
                <Textarea data-testid="story-quote" rows={5} value={story.quote} onChange={(e) => setStory({ ...story, quote: e.target.value })} placeholder="Guardian360 helped me when…" />
              </div>
              <div>
                <Label>City (optional)</Label>
                <Input data-testid="story-city" value={story.city} onChange={(e) => setStory({ ...story, city: e.target.value })} placeholder="Mumbai" />
              </div>
              <div>
                <Label>Rating</Label>
                <div className="flex gap-1 mt-1" data-testid="story-stars">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} type="button" onClick={() => setStory({ ...story, stars: n })} className={`text-2xl ${n <= story.stars ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>★</button>
                  ))}
                </div>
              </div>
              <Button data-testid="submit-story-btn" onClick={submitStory} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
                Submit for review
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <Activity className="w-3.5 h-3.5 text-[hsl(var(--secondary))]" />
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
